'use client';
import cn from 'classnames';
import { useId } from 'react';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Testimonial, TestimonialProps } from '@/ui-components/molecules/Testimonial/Testimonial';
import { responsiveBreakpointsPx } from '@/constants';
import { useToggleWithButton } from '@/hooks/useToggleWithButton';
import { useViewportWidthName } from '@/hooks/useViewportSizeName';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import styles from './testimonialsmasonrygroup.module.scss';

export interface TestimonialsMasonryGroupProps {
    testimonials: TestimonialProps[];
    maxColumns?: number;
}

export function TestimonialsMasonryGroup({
    testimonials,
    maxColumns = 3,
}: TestimonialsMasonryGroupProps): React.JSX.Element {
    const viewportWidthName = useViewportWidthName();
    const columnCalcCountIndex = (testimonials.length > 3 ? '4+' : testimonials.length) as ColumnCalcCountMapIndex;
    const columnCount = Math.min(maxColumns, columnCountMap[`${viewportWidthName}-${columnCalcCountIndex}`]);
    const columnCountCustomProperty: CSSPropsVars = {
        '--testimonialGroupColumnCount': String(columnCount),
    };

    const [ShowMoreButton, mobileIsExpanded] = useToggleWithButton(false);
    const groupWrapperId = useId();

    return (
        <div>
            <div
                className={cn(styles.groupWrapper, { [styles.mobileExpanded]: mobileIsExpanded })}
                style={columnCountCustomProperty}
                id={groupWrapperId}
            >
                {testimonials.map((testimonialProps, index) => (
                    <div className={styles.item} key={index}>
                        <Testimonial {...testimonialProps} excludeResponsiveSizes={['md', 'lg']} />
                    </div>
                ))}
            </div>

            {testimonials.length > 4 && (
                <div className={styles.showMoreButtonWrapper}>
                    <ShowMoreButton fitting="hug-contents" ariaControls={groupWrapperId}>
                        <div className={styles.showMoreButtonContents}>
                            <Text tag="span" variant="text-xlarge">
                                {mobileIsExpanded ? 'Show less' : 'Show more'}
                            </Text>

                            <div className={styles.chevronIconWrapper}>
                                <ChevronIcon direction={mobileIsExpanded ? 'up' : 'down'} />
                            </div>
                        </div>
                    </ShowMoreButton>
                </div>
            )}
        </div>
    );
}

type ColumnCalcViewportSizeNameIndex = keyof typeof responsiveBreakpointsPx;
type ColumnCalcCountMapIndex = '1' | '2' | '3' | '4+';

const columnCountMap: Readonly<{
    [index in `${ColumnCalcViewportSizeNameIndex}-${ColumnCalcCountMapIndex}`]: number;
}> = {
    'sm-1': 1,
    'sm-2': 1,
    'sm-3': 1,
    'sm-4+': 1,

    'md-1': 1,
    'md-2': 2,
    'md-3': 2,
    'md-4+': 2,

    'lg-1': 1,
    'lg-2': 2,
    'lg-3': 3,
    'lg-4+': 3,

    'xl-1': 1,
    'xl-2': 2,
    'xl-3': 3,
    'xl-4+': 3,

    'nav-tablet-1': 1,
    'nav-tablet-2': 2,
    'nav-tablet-3': 3,
    'nav-tablet-4+': 3,
};
